import React from 'react';
import { OffersHeroV2Props } from './OffersHeroV2.types';
import { Hero } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';
import { StyledOffersHeroV2 } from './OffersHeroV2.styles';
import {
  decodeHtml,
  getMediaData,
  getMediaSource,
  isUserAuthenticated,
  normalizeOffersDataNode,
} from '../../utils/OfferUtils';
import { PRIMARY_MEDIA, SECONDARY_MEDIA, WIDE_HOR_IMG, PANO_HOR_IMG } from '../../constants/CommonConstants';
import { ImageRenditionsData } from '../../utils/ImageRenditionStructure';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

export const OffersHeroV2Wrapper: React.FC<OffersHeroV2Props> = props => {
  const { model, offersData, isAuthorMode } = props;
  const { offersHeroV2 } = ImageRenditionsData;
  const { envVarsObject } = useClientEnvVarsStore();
  const { datalayer } = useSession();
  const hasResolvedAuthState =
    datalayer != null && (Object.prototype.hasOwnProperty.call(datalayer, 'memState') || 'mr_id' in datalayer);
  const isUserLoggedIn = isUserAuthenticated(datalayer);
  const imageDomain = envVarsObject?.['UXL_IMAGE_CACHE_DOMAIN'];
  const offersDataNode = normalizeOffersDataNode(offersData);
  const offersDataMedia = offersDataNode?.node?.media;
  const mediaSource = isAuthorMode
    ? 'model'
    : getMediaSource(offersDataMedia, PRIMARY_MEDIA, offersHeroV2?.aspectRatio?.dynamicMedia);
  const isMediaFromDAC = mediaSource === PRIMARY_MEDIA;
  const media = isAuthorMode
    ? { rendition: [], fileReferenceBackgroundImage: '' }
    : getMediaData(
        offersDataMedia,
        offersHeroV2?.dynamicMedia,
        mediaSource,
        PRIMARY_MEDIA,
        imageDomain,
        model.variation === '3:1' ? PANO_HOR_IMG : WIDE_HOR_IMG
      );

  const logoMediaSource = isAuthorMode
    ? 'model'
    : getMediaSource(offersDataMedia, SECONDARY_MEDIA, offersHeroV2?.aspectRatio?.dynamicMediaLogo);
  const isLogoMediaFromDAC = logoMediaSource === SECONDARY_MEDIA;
  const logoMedia = isAuthorMode
    ? { rendition: [], fileReferenceBackgroundImage: '' }
    : getMediaData(offersDataMedia, offersHeroV2?.dynamicMediaLogo, logoMediaSource, SECONDARY_MEDIA, imageDomain);

  const resolvedSecondaryCtaText =
    offersDataNode?.merchandisingNode?.secondaryCallToActionLabel ?? model?.secondaryCtaText;
  const resolvedSecondaryCtaLink =
    offersDataNode?.merchandisingNode?.secondaryCallToActionLink ?? model?.secondaryCtaLink;
  const isSignInSecondaryCta = resolvedSecondaryCtaText?.trim()?.toLowerCase() === 'sign in';
  const hideSecondaryCta = isSignInSecondaryCta && (!hasResolvedAuthState || isUserLoggedIn);
  const resolvedStyleclass =
    offersDataNode?.merchandisingNode?.theme == null
      ? model?.styleclass ?? 'standard'
      : offersDataNode?.merchandisingNode?.theme === 'Dark'
      ? 'inverse'
      : 'standard';
  const resolvedEyebrowText = decodeHtml(offersDataNode?.node?.descriptionTeaser) || model?.eyebrowText;

  return (
    <StyledOffersHeroV2 data-component-name="o-offers-OffersHeroV2" data-testid="OffersHeroV2">
      <Hero
        eyebrowText={resolvedEyebrowText}
        iconAltText={''}
        header={offersDataNode?.node?.title ? decodeHtml(offersDataNode.node.title) : model?.header}
        description={decodeHtml(offersDataNode?.node?.description) || model?.description}
        primaryCtaLink={offersDataNode?.node?.callToActionLink || model?.primaryCtaLink}
        primaryCtaText={offersDataNode?.node?.callToActionLabel || model?.primaryCtaText}
        primaryCtaOpenInNewTab={model?.primaryCtaOpenInNewTab}
        secondaryCtaLink={hideSecondaryCta ? undefined : resolvedSecondaryCtaLink}
        secondaryCtaText={hideSecondaryCta ? undefined : resolvedSecondaryCtaText}
        secondaryCtaOpenInNewTab={model?.secondaryCtaOpenInNewTab}
        iconText={offersDataNode?.merchandisingNode?.caption || model?.iconText}
        styleclass={resolvedStyleclass}
        dynamicMedia={isMediaFromDAC ? media : model?.dynamicMedia}
        dynamicMediaLogo={isLogoMediaFromDAC ? logoMedia : model?.dynamicMediaLogo}
        eyebrowType={model?.eyebrowType}
        assetType={model?.assetType}
        variation={model?.variation}
        linkUrl={offersDataNode?.merchandisingNode?.captionUrl ?? model?.linkUrl}
        iconFontPath={model?.iconFontPath}
        iconPath={model?.iconPath}
      />
    </StyledOffersHeroV2>
  );
};
